import { useEffect, useRef, useState } from "react";
import { ApiError, PayrollValidationError, calculatePaidLeave } from "@/api/client";
import type {
  PaidLeaveResponse,
  PaidLeaveEmployeeResult,
  PaidLeaveProgramOverrideRequest,
  PayrollEmployee,
} from "@/api/types";
import { CHECK_DATE, COMPANY_STATE, TAX_YEAR } from "@/pages/payroll-fixtures";
import { describeReadFailure } from "@/lib/apiFailure";

export type PaidLeaveRowStatus = "loading" | "ready" | "error";

export interface PaidLeaveRow {
  employee: PayrollEmployee;
  status: PaidLeaveRowStatus;
  result: PaidLeaveEmployeeResult | null;
  error: string | null;
}

export type PaidLeaveStatus = "loading" | "ready" | "failed";

export interface UsePaidLeaveResult {
  rows: PaidLeaveRow[];
  status: PaidLeaveStatus;
  /**
   * The whole engine response — program parameters, totals and the quarterly wage
   * detail — exactly as returned. Null until it resolves, and null if the request
   * itself failed. Nothing in this app recomputes any part of it.
   */
  data: PaidLeaveResponse | null;
  /** Set only for a REQUEST-level failure (network down, unconfigured state/year,
   *  program parameters the engine refused). */
  error: string | null;
  /**
   * True when `error` is the engine REJECTING the request (a 400 with reasons) rather
   * than the engine being unreachable. The two need different words on screen: "we could
   * not reach it" is wrong and unactionable when what actually happened is "you typed a
   * negative rate", and a booth visitor who reads the first will stop rather than fix it.
   */
  rejected: boolean;
  erroredCount: number;
}

/** Turns a row's field->messages map into one readable string — same join
 *  PayrollValidationError uses, so a per-line failure and a whole-run failure read
 *  the same way on screen. */
function messageFor(errors: Record<string, string[]> | null): string {
  if (!errors) return "Could not reach the paid-leave engine.";
  return Object.values(errors).flat().join(" ") || "The paid-leave engine rejected this line.";
}

/**
 * Fires ONE POST /api/payroll/paid-leave for the whole roster and hands back the
 * engine's answer untouched.
 *
 * Deliberately mirrors usePayrollRun's shape (one call, per-row status, honest error
 * text taken from the server rather than invented here) — the two screens should fail
 * the same way, because a farmer who learns what a red row means on one screen should
 * not have to learn it again on the other.
 *
 * What this hook does NOT do is as important as what it does: it never sums, never
 * applies a rate, and holds no copy of the MN percentages or wage base. Every figure
 * the Paid Leave screen renders — including the program parameters it prints — came
 * out of `data`.
 *
 * `programOverride` does not change that. It is the employer's own rates travelling OUT
 * to the engine, which then computes with them and sends the result back like any other
 * run — the screen still renders only what came back. An override reaches the totals and
 * the wage-detail file because the ENGINE applied it, not because anything here
 * recalculated. Null means "use the published defaults", which is what every caller sent
 * before this parameter existed.
 */
/**
 * @param employees The roster from usePayrollRoster, or `null` while it is still loading /
 * failed to load. As in usePayrollRun, `null` (NOT KNOWN YET) and `[]` (nobody on this
 * payroll) are deliberately different, and neither is sent to the engine.
 */
export function usePaidLeave(
  employees: PayrollEmployee[] | null,
  programOverride: PaidLeaveProgramOverrideRequest | null = null,
): UsePaidLeaveResult {
  const [rows, setRows] = useState<PaidLeaveRow[]>([]);
  const [data, setData] = useState<PaidLeaveResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rejected, setRejected] = useState(false);
  const [pending, setPending] = useState(true);
  /** The last response the engine ACCEPTED, tracked outside render state so the failure
   *  path can ask "was there ever a good run?" without reading `data` through a stale
   *  closure or doing work inside a state updater. */
  const acceptedRef = useRef<PaidLeaveResponse | null>(null);

  useEffect(() => {
    if (employees === null || employees.length === 0) {
      setRows([]);
      setData(null);
      setError(null);
      setRejected(false);
      // An un-fetched roster is still pending; an empty one is a finished answer.
      setPending(employees === null);
      acceptedRef.current = null;
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    setPending(true);
    setRows(
      employees.map((employee) => ({
        employee,
        status: "loading" as const,
        result: null,
        error: null,
      })),
    );

    calculatePaidLeave(
      {
        state: COMPANY_STATE,
        taxYear: TAX_YEAR,
        // Null unless the employer set their own — see the class remarks on
        // PaidLeaveRequest.Program for why this exists at all.
        program: programOverride,
        // year/quarter omitted on purpose: the SERVER derives the reporting quarter
        // from the check dates, through the engine's own QuarterOf. This app deciding
        // "August is Q3" would be this app doing the engine's job.
        employees: employees.map((employee) => ({
          employeeId: employee.id,
          // The roster carries display names ("Miguel A."), not the first/last split
          // MN's file wants. Sent as the last name with no invented first name rather
          // than guessing at a split — the screen says so.
          lastName: employee.name,
          ssn: null,
          checkDate: CHECK_DATE,
          grossWages: employee.grossWages,
          // The demo roster has no pre-tax deductions, so covered wages equal gross.
          // A real run would send post-pretax wages here when the company's
          // covered_basis says so — the engine takes either.
          coveredWages: employee.grossWages,
          // No YTD history exists in this demo, so the annual wage-base cap is not
          // exercised. Disclosed on screen rather than passed off as "no cap applies".
          ytdCoveredWages: 0,
          // NOT employee.ficaExemptByLaw — see the doc on PayrollEmployee in api/types.ts.
          // FICA exemption and paid-leave exemption are different determinations, and this
          // repo has not made the second one.
          exempt: false,
          hours: employee.hoursThisPeriod,
        })),
      },
      controller.signal,
    )
      .then((response) => {
        if (cancelled) return;
        const byId = new Map(response.employees.map((r) => [r.employeeId, r]));
        setRows(
          employees.map((employee) => {
            const result = byId.get(employee.id);
            if (!result) {
              // The engine echoes back exactly the employeeIds it was given. A missing
              // row means the response dropped a roster line, which is the corrupted-run
              // failure mode this whole shape guards against — surfaced, never rendered
              // as a plausible zero.
              return {
                employee,
                status: "error" as const,
                result: null,
                error: "The paid-leave engine did not return a result for this employee.",
              };
            }
            return result.success
              ? { employee, status: "ready" as const, result, error: null }
              : { employee, status: "error" as const, result, error: messageFor(result.errors) };
          }),
        );
        acceptedRef.current = response;
        setData(response);
        setError(null);
        setRejected(false);
        setPending(false);
      })
      .catch((err: unknown) => {
        if (cancelled || (err instanceof DOMException && err.name === "AbortError")) return;
        const wasRejected = err instanceof PayrollValidationError;
        const message =
          err instanceof PayrollValidationError
            ? // A PayrollValidationError's message is the server's own reason — "hours must
              // be positive" and the like — which is exactly what the reader needs. Kept.
              err.message
            : err instanceof ApiError
              ? // An ApiError's message is NOT: `${status} ${statusText}: ${body}` at every
                // throw site in api/client.ts. See lib/apiFailure.ts for the 502 that
                // reached the Live Wall's live region reading "502 Bad Gateway".
                describeReadFailure(err)
              : "Could not reach the paid-leave engine.";
        setError(message);
        setRejected(wasRejected);
        setPending(false);

        // A REJECTION keeps the last accepted run on screen. The engine refusing a rate
        // the employer just typed says nothing about the run it already computed, and
        // blanking a correct calculation to report a typo throws away information the
        // screen already had — the figures below stay the ones the engine last accepted,
        // which the banner says out loud. Anything else (unreachable, 5xx, a dropped
        // roster) still clears, because then we genuinely do not know the answer.
        if (wasRejected && acceptedRef.current !== null) return;

        acceptedRef.current = null;
        setRows(
          employees.map((employee) => ({
            employee,
            status: "error" as const,
            result: null,
            error: message,
          })),
        );
        setData(null);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
    // `employees` IS a real dependency now that the roster is fetched (usePayrollRoster)
    // rather than imported as a module-level constant — same change as usePayrollRun. Its
    // identity changes exactly once, when the roster arrives, which is when the run must
    // fire; the roster hook holds the array in state, so this never re-fires on a plain
    // re-render.
    //
    // `programOverride` IS a real dependency: a new object identity means the employer
    // applied different rates and the whole run must be recomputed BY THE ENGINE. The
    // caller holds it in state and only replaces it on an explicit Apply/Reset, so this
    // re-fires exactly when the parameters changed and never on a re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, programOverride]);

  const erroredCount = rows.filter((r) => r.status === "error").length;
  const status: PaidLeaveStatus = pending || rows.some((r) => r.status === "loading")
    ? "loading"
    : error !== null
      ? "failed"
      : "ready";

  return { rows, status, data, error, rejected, erroredCount };
}
